import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { FaTwitter, FaLinkedinIn, FaFacebookF, FaInstagram } from 'react-icons/fa';
import Logo from './Logo';
import { theme } from '../../styles/GlobalStyles';

const FooterContainer = styled.footer`
  background-color: ${theme.surface};
  padding: 5rem 0 2rem;
  position: relative;
  border-top: 1px solid ${theme.border};

  &::before {
    content: "";
    position: absolute;
    top: 0; left: 50%;
    transform: translateX(-50%);
    width: 50%; height: 1px;
    background: linear-gradient(to right, transparent, rgba(97,93,255,0.5), transparent);
  }
`;

const FooterContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr;
  gap: 2.5rem;
  padding: 0 2rem;

  @media (max-width: 992px) { grid-template-columns: 1fr 1fr; }
  @media (max-width: 576px) { grid-template-columns: 1fr; }
`;

const Column = styled.div``;

const Description = styled.p`
  color: ${theme.textMuted};
  margin: 1rem 0 1.5rem;
  max-width: 300px;
  font-size: 0.9rem;
  line-height: 1.7;
`;

const SocialIcons = styled.div`
  display: flex;
  gap: 0.65rem;
`;

const SocialIcon = styled.a`
  width: 36px; height: 36px;
  border-radius: 9px;
  background: rgba(255,255,255,0.04);
  border: 1px solid ${theme.border};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${theme.textMuted};
  font-size: 0.8rem;
  transition: all 0.25s ease;
  cursor: pointer;

  &:hover {
    background: rgba(97,93,255,0.15);
    border-color: rgba(97,93,255,0.4);
    color: #fff;
    transform: translateY(-2px);
  }
`;

const ColTitle = styled.h3`
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: ${theme.textDim};
  margin-bottom: 1.2rem;
`;

const LinkList = styled.ul`
  list-style: none;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
`;

const LinkItem = styled.li`
  a {
    color: ${theme.textMuted};
    font-size: 0.875rem;
    transition: color 0.2s;
    &:hover { color: ${theme.text}; }
  }
`;

const BottomBar = styled.div`
  max-width: 1200px;
  margin: 3rem auto 0;
  padding: 1.5rem 2rem 0;
  border-top: 1px solid ${theme.border};
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;

  @media (max-width: 600px) { justify-content: center; }
`;

const Copyright = styled.span`
  color: ${theme.textDim};
  font-size: 0.82rem;
`;

const PolicyLinks = styled.div`
  display: flex;
  gap: 1.5rem;
  a {
    color: ${theme.textDim};
    font-size: 0.82rem;
    transition: color 0.2s;
    &:hover { color: ${theme.textMuted}; }
  }
`;

const Footer = () => (
  <FooterContainer>
    <FooterContent>
      <Column>
        <Logo />
        <Description>
          Prodseal provides industry-leading product authentication and warranty solutions to protect your brand and empower your customers.
        </Description>
        <SocialIcons>
          <SocialIcon href="https://twitter.com/prodseal" target="_blank" rel="noopener noreferrer" aria-label="Twitter"><FaTwitter /></SocialIcon>
          <SocialIcon href="https://linkedin.com/company/prodseal" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn"><FaLinkedinIn /></SocialIcon>
          <SocialIcon href="https://facebook.com/prodseal" target="_blank" rel="noopener noreferrer" aria-label="Facebook"><FaFacebookF /></SocialIcon>
          <SocialIcon href="https://instagram.com/prodseal" target="_blank" rel="noopener noreferrer" aria-label="Instagram"><FaInstagram /></SocialIcon>
        </SocialIcons>
      </Column>

      <Column>
        <ColTitle>Company</ColTitle>
        <LinkList>
          <LinkItem><Link to="/about">About Us</Link></LinkItem>
          <LinkItem><Link to="/about">Our Vision</Link></LinkItem>
          <LinkItem><Link to="/trust-compliance">Trust & Compliance</Link></LinkItem>
          <LinkItem><Link to="/contact">Contact Us</Link></LinkItem>
        </LinkList>
      </Column>

      <Column>
        <ColTitle>Solutions</ColTitle>
        <LinkList>
          <LinkItem><Link to="/solutions">Authentication</Link></LinkItem>
          <LinkItem><Link to="/solutions">Smart QR Codes</Link></LinkItem>
          <LinkItem><Link to="/solutions">Warranty Seal</Link></LinkItem>
          <LinkItem><Link to="/solutions">Analytics</Link></LinkItem>
          <LinkItem><Link to="/solutions">Consumer Verification</Link></LinkItem>
        </LinkList>
      </Column>

      <Column>
        <ColTitle>Resources</ColTitle>
        <LinkList>
          <LinkItem><Link to="/resources">Blog</Link></LinkItem>
          <LinkItem><Link to="/resources">Case Studies</Link></LinkItem>
          <LinkItem><Link to="/resources">Whitepapers</Link></LinkItem>
          <LinkItem><Link to="/how-it-works">How It Works</Link></LinkItem>
          <LinkItem><Link to="/demo">Live Demo</Link></LinkItem>
        </LinkList>
      </Column>
    </FooterContent>

    <BottomBar>
      <Copyright>© {new Date().getFullYear()} Prodseal. All rights reserved.</Copyright>
      <PolicyLinks>
        <Link to="/privacy">Privacy Policy</Link>
        <Link to="/terms">Terms of Service</Link>
        <Link to="/cookies">Cookies</Link>
      </PolicyLinks>
    </BottomBar>
  </FooterContainer>
);

export default Footer;
